import type { HeatDay } from "@/hooks/useMotivation";

interface Props {
  heatmap: HeatDay[];
  loading: boolean;
}

function heatColor(minutes: number): string {
  if (minutes <= 0) return "bg-background-100";
  if (minutes < 30) return "bg-primary-300";
  if (minutes < 60) return "bg-primary-500";
  if (minutes < 120) return "bg-primary-600";
  return "bg-primary-700";
}

function tooltip(day: HeatDay): string {
  const d = new Date(`${day.date}T00:00:00`);
  const label = d.toLocaleDateString("tr-TR", { day: "numeric", month: "long" });
  if (day.minutes <= 0) return `${label}: çalışma yok`;
  return `${label}: ${day.minutes} dakika`;
}

const DAY_LABELS = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];

export default function StudyHeatmap({ heatmap, loading }: Props) {
  if (loading) {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h3 className="font-heading font-semibold text-base text-foreground-950">Çalışma Takvimi</h3>
        <div className="mt-4 h-32 rounded-md bg-background-100 animate-pulse" />
      </div>
    );
  }

  if (heatmap.length === 0) return null;

  // GitHub tarzı haftalık grid: ilk günün hafta içi konumuna göre hizala (Pazar=0).
  const first = heatmap[0];
  const offset = first ? first.dayOfWeek : 0;
  const cells: Array<HeatDay | null> = [];
  for (let i = 0; i < offset; i += 1) cells.push(null);
  heatmap.forEach((d) => cells.push(d));
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: Array<Array<HeatDay | null>> = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  const activeDays = heatmap.filter((d) => d.minutes > 0).length;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-base text-foreground-950">Çalışma Takvimi</h3>
        <span className="text-xs text-foreground-500">son 20 hafta · {activeDays} aktif gün</span>
      </div>

      <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
        {/* Gün etiketleri */}
        <div className="flex flex-col gap-1 shrink-0">
          {DAY_LABELS.map((label, i) => (
            <span key={label} className="h-3.5 text-[9px] leading-none text-foreground-400">
              {i % 2 === 0 ? label : ""}
            </span>
          ))}
        </div>

        {/* Haftalar */}
        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-1 shrink-0">
            {week.map((day, di) =>
              day ? (
                <div
                  key={di}
                  title={tooltip(day)}
                  className={`w-3.5 h-3.5 rounded-[3px] ${heatColor(day.minutes)} cursor-pointer`}
                />
              ) : (
                <div key={di} className="w-3.5 h-3.5" />
              ),
            )}
          </div>
        ))}
      </div>

      {/* Lejant */}
      <div className="mt-3 flex items-center justify-end gap-1.5">
        <span className="text-[10px] text-foreground-500">Az</span>
        <span className="w-3.5 h-3.5 rounded-[3px] bg-background-100" />
        <span className="w-3.5 h-3.5 rounded-[3px] bg-primary-300" />
        <span className="w-3.5 h-3.5 rounded-[3px] bg-primary-500" />
        <span className="w-3.5 h-3.5 rounded-[3px] bg-primary-600" />
        <span className="w-3.5 h-3.5 rounded-[3px] bg-primary-700" />
        <span className="text-[10px] text-foreground-500">Çok</span>
      </div>
    </div>
  );
}